import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import api from '../../../utils/api';
import { GET_ALL_SCHOLARSHIP_APPLICATION_QUERY_KEY } from './useGetAllScholarshipApplicationQuery';

export interface ScholarshipStageActionPayload {
  scholarshipApplicationId: number;
  stageOrder: number;
  notes: string;
  scholarshipPercent: number;
}

type StageAction = 'approve-stage' | 'reject' | 'waitlist';

const postStageAction = (id: number, action: StageAction, payload: ScholarshipStageActionPayload) =>
  api.post(`/admin/scholarship/${id}/${action}`, payload).then((r) => r?.data);

export const useScholarshipStageActionsMutation = (handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: GET_ALL_SCHOLARSHIP_APPLICATION_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: ['get-scholarship-pending-by-stage'] });
  };

  const approveMutation = useMutation({
    mutationKey: ['scholarship-approve-stage'],
    mutationFn: ({ id, payload }: { id: number; payload: ScholarshipStageActionPayload }) =>
      postStageAction(id, 'approve-stage', payload),
  });

  const rejectMutation = useMutation({
    mutationKey: ['scholarship-reject'],
    mutationFn: ({ id, payload }: { id: number; payload: ScholarshipStageActionPayload }) =>
      postStageAction(id, 'reject', payload),
  });

  const waitlistMutation = useMutation({
    mutationKey: ['scholarship-waitlist'],
    mutationFn: ({ id, payload }: { id: number; payload: ScholarshipStageActionPayload }) =>
      postStageAction(id, 'waitlist', payload),
  });

  const execute = async (
    mutation: typeof approveMutation,
    id: number,
    payload: ScholarshipStageActionPayload,
    successMsg: string,
  ) => {
    try {
      const data = await mutation.mutateAsync({ id, payload });
      notification.success({ message: data?.message || successMsg });
      invalidateQueries();
      handleClose?.();
    } catch (err: any) {
      notification.error({
        message: 'Error',
        description:
          err?.response?.data?.message || err?.response?.data?.title || 'Action failed',
      });
    }
  };

  return {
    approve: (id: number, payload: ScholarshipStageActionPayload) =>
      execute(approveMutation, id, payload, 'Scholarship application approved successfully'),
    reject: (id: number, payload: ScholarshipStageActionPayload) =>
      execute(rejectMutation, id, payload, 'Scholarship application rejected'),
    waitlist: (id: number, payload: ScholarshipStageActionPayload) =>
      execute(waitlistMutation, id, payload, 'Scholarship application waitlisted'),
    isApproving: approveMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isWaitlisting: waitlistMutation.isPending,
  };
};
